import { Button, Modal, Select, TextInput, NumberInput, Flex, Image, Badge } from '@mantine/core';
import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import logo from '../assets/logo.png';
import 'react-toastify/dist/ReactToastify.css';
import * as messages from './messages/viewFormText';
import {frequencyDays} from './messages/viewFormText';
import { SurveyCreatorComponent, SurveyCreator, Serializer, JsonView, LogicEditor, translateFromSurveyJS, translateToSurveyJS } from '@navriti/forms';
import { uploadFileToStorage } from '../utils/fileStorage'; 
import { URLs } from './messages/apiUrls';
import FormVersionHistory from '../components/FormVersionHistory';
import {
  BASE_URL,
  URL_GET_TEMPLATE,
  URL_ARRAY_FORM,
  URL_FORM_SAVE,
  URL_UPDATE_LINK,
} from './messages/apiUrls';
import './survey.css';
const creatorOptions = {
  showLogicTab: true,
  isAutoSave: true,
  showJSONEditorTab: true,
  //excludeQuestionTypes: ['text', 'comment', 'checkbox', 'radiogroup', 'dropdown', 'boolean', 'ranking'],
};

Serializer.findProperty("file", "maxSize").visible = false;

// Override the property editor behavior
Serializer.addProperty("file", {
  name: "maxSizeInMB:number",
  category: "general",
  visibleIndex: 15,
  displayName: "Maximum file size (MB)",
  description: "Enter file size in megabytes",
  default: 0,
  onSetValue: (obj, value) => {
    obj.maxSize = value * 1024 * 1024; // MB → bytes
  },
  onGetValue: (obj) => {
    return obj.maxSize ? obj.maxSize / (1024 * 1024) : 0;
  }
});
Serializer.findProperty("file", "acceptedTypes").visible = false;

const FILE_TYPE_MAP = {
  images: [".jpg", ".jpeg", ".png"],
  pdf: [".pdf"],
  word: [".doc", ".docx"],
  excel: [".xls", ".xlsx"],
  video: [".mp4"],
  zip: [".zip"]
};

Serializer.addProperty("file", {
  name: "acceptedTypesList",
  visibleIndex: 15,
  // type: "checkbox",
  isArray: true,
  category: "general",
  title: "Accepted file types",
    type: "multiplevalues",
  visible: true,
  choices: [
    { value: "images", text: "Images (.jpg, .png)" },
    { value: "pdf", text: "PDF (.pdf)" },
    { value: "word", text: "Word (.doc, .docx)" },
    { value: "excel", text: "Excel (.xls, .xlsx)" },
    { value: "video", text: "Videos (.mp4)" },
    { value: "zip", text: "Zip (.zip)" }
  ],
  default: [],

  onSetValue: (obj, value) => {
  // Normalize value to array
    const list = Array.isArray(value)
      ? value
      : value
        ? [value]
        : [];

    if (list.length === 0) {
      obj.acceptedTypes = "";
      return;
    }

    const extensions = list.flatMap(v => FILE_TYPE_MAP[v] || []);
    obj.acceptedTypes = extensions.join(",");
  },


  onGetValue: (obj) => {
    if (!obj.acceptedTypes  || typeof obj.acceptedTypes !== "string") return [];

    const current = obj.acceptedTypes.split(",");

    return Object.entries(FILE_TYPE_MAP)
      .filter(([, exts]) => exts.some(e => current.includes(e)))
      .map(([key]) => key);
  }
});

const defaultJson = {
  pages: [
    {
      name: 'Name',
      elements: [
        {
          name: 'FirstName',
          title: 'Enter your first name:',
          type: 'text',
        },
        {
          name: 'LastName',
          title: 'Enter your last name:',
          type: 'text',
        },
      ],
    },
  ],
};

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
}


export default function SurveyCreatorWidget() {
  const [formData, setFormData] = useState(null);
  const [load, setLoad] = useState(false);
  const [creator, setCreator] = useState(null);
  const [activeTab, setActiveTab] = useState('designer');
  const [navritiSchema, setNavritiSchema] = useState({ metadata: {}, pages: [], rules: [] });
  const [link, setLink] = useState('');
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [type, setType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [duration, setDuration] = useState(1);
  const [frequency, setFrequency] = useState('');
  const [error, setError] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [templateData, setTemplateData] = useState(null);
  const [id, setId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [isPublishing, setIsPublishing] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [accessRole, setAccessRole] = useState("OWNER");
  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const navritiSchemaRef = useRef({ metadata: {}, pages: [], rules: [] });
  const logoRef = useRef('');
  const [designerKey, setDesignerKey] = useState(0);
  const hiddenQuestionsRef = useRef({});

  const handleTabChange = (tab) => {
  if ((tab === 'logic' || tab === 'json') && creator) {
    try {
      const sjsJson = JSON.parse(creator.text);
      logoRef.current = sjsJson.logo || logoRef.current;
      const hiddenMap = {};
      (sjsJson.pages || []).forEach((page) => {
         const hidden = (page.elements || []).filter(el => el.visible === false || el.name?.startsWith('__'));
         if (hidden.length > 0) {
           hiddenMap[page.name] = hidden;
          }
      });
      hiddenQuestionsRef.current = hiddenMap;
      const pages = translateFromSurveyJS(sjsJson);
      const translated = {
        metadata: { title: sjsJson.title || '', description: sjsJson.description || '', logo: sjsJson.logo || '' },
        pages: pages,
        rules: [],
        settings: {
          oneResponsePerEmail: !!sjsJson.oneResponsePerEmail,
          limitOneResponse: !!sjsJson.limitOneResponse,
          readOnly: !!sjsJson.readOnly,
          language: sjsJson.locale || 'en',
          showTitleDescription: sjsJson.showTitleDescription !== undefined ? sjsJson.showTitleDescription : true,
          widthMode: sjsJson.widthMode || 'auto',
        },
      };
      setNavritiSchema(translated);
      navritiSchemaRef.current = translated;
    } catch (e) {
      console.error('Error syncing navritiSchema on tab change:', e);
    }
  }
   if (tab === 'designer') {
    setDesignerKey(prev => prev + 1);
  }
  setActiveTab(tab);
};

const handleLogoUpload = async (...args) => {
  const file = args[0];
  try {
    const token = getCookie('token');
    const uploaded = await uploadFileToStorage({ file, formId: id || undefined, token });
    const streamUrl = new URL(`${process.env.REACT_APP_API_BASE_URL}/files/stream`);
    streamUrl.searchParams.set('path', uploaded.path);
    return streamUrl.toString();
  } catch (err) {
    console.error('Error uploading logo:', err);
    toast.error('Failed to upload logo.');
    throw err;
  }
};

  const fetchDecryptedId = async (encryptedId) => {
  try {
    const token = getCookie('token');
    if (!token) throw new Error('No authentication token found');
    
    console.log('Attempting to decrypt:', encryptedId);
    
    const response = await fetch(messages.DECRYPT_ID, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ encryptedId }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Decryption failed:', errorData);
      throw new Error(errorData.error || messages.DECRYPT_ID_FAIL);
    }

    const decryptedData = await response.json();
    const decryptedId = decryptedData.decryptedId;
    
    // Validate that we got a numeric ID
    if (!decryptedId || isNaN(parseInt(decryptedId))) {
      throw new Error('Decryption returned invalid ID: ' + decryptedId);
    }
    
    console.log('Successfully decrypted to:', decryptedId);
    return parseInt(decryptedId);
  } catch (error) {
    console.error('Error decrypting ID:', error.message);
    throw error; // Re-throw so the calling code can handle it
  }
};

  const getTokenFromCookie = () => {
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find((cookie) => cookie.trim().startsWith('token='));
    const token = tokenCookie ? tokenCookie.split('=')[1] : null;
    return token;
  };

  useEffect(() => {
    const initializeSurveyCreator = async () => {
  try {
    const params = new URLSearchParams(location.search);
    const ID = params.get('id');
    const TemplateID = params.get('TemplateId');

    const token = getTokenFromCookie();
    if (!token) {
      throw new Error('User not authenticated. Please log in.');
    }

    const surveyCreator = new SurveyCreator(creatorOptions);

    const hiddenTypes = [
        "multipletext",
        "paneldynamic",
        "matrix",
        "matrixdropdown",
        "matrixdynamic",
        "matrixrating",
        "imagepicker"
      ];

      hiddenTypes.forEach(type => {
        surveyCreator.toolbox.removeItem(type);
      });

    let initialJson = JSON.stringify(defaultJson); // Ensure defaultJson is a valid object

    if (ID) {
      const id = await fetchDecryptedId(ID);
      setId(id);
      const formDataResponse = await fetch(URL_ARRAY_FORM(id), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (formDataResponse.ok) {
        const data = await formDataResponse.json();
        setAccessRole(data.accessRole || "OWNER");
        console.log('Form data:', data); // Debug log
        // Ensure formData is a string or valid object
        if (data.formData) {
          initialJson =
            typeof data.formData === 'string'
              ? data.formData
              : JSON.stringify(data.formData); // Convert object to string
        } else {
          initialJson = JSON.stringify(defaultJson);
        }
        setFormData(data);
        setType(data.type || '');
        if (data.recurrence) {
          setDuration(data.recurrence.duration || 1);
          setFrequency(data.recurrence.frequency || '');
        } else if (data.dateRange && data.dateRange.length === 2) {
          setStartDate(new Date(data.dateRange[0]).toISOString().split('T')[0]);
          setEndDate(new Date(data.dateRange[1]).toISOString().split('T')[0]);
        }
        if (!data.isDraft && data.formLink) {
          setLink(data.formLink);
        } else if (!data.isDraft) {
          const encryptedId = await encryptFormId(id);
          const baseUrl = new URL(window.location.href).origin;
          setLink(`${baseUrl}/forms?id=${encryptedId}`);
        }
      } else {
        throw new Error('Failed to fetch form data');
      }
    } else if (TemplateID) {
      const templateId = await fetchDecryptedId(TemplateID);
      setTemplateId(templateId);
      const templateResponse = await fetch(URL_GET_TEMPLATE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ TemplateId: templateId }),
      });

      if (templateResponse.ok) {
        const template = await templateResponse.json();
        console.log('Template data:', template); // Debug log
        // Ensure templateData.TemplateData is a string or valid object
        if (template.templateData?.TemplateData) {
          initialJson =
            typeof template.templateData.TemplateData === 'string'
              ? template.templateData.TemplateData
              : JSON.stringify(template.templateData.TemplateData); // Convert object to string
        } else {
          initialJson = JSON.stringify(defaultJson);
        }
        setTemplateData(template);
      } else {
        throw new Error('Failed to fetch template data');
      }
    }

    // Ensure initialJson is a string before assigning
    console.log('initialJson before assignment:', initialJson, typeof initialJson);
    if (typeof initialJson !== 'string') {
      console.warn('initialJson is not a string, converting to string:', initialJson);
      initialJson = JSON.stringify(initialJson);
    }
    surveyCreator.text = initialJson;
        
    (function attachDescriptionLimit(creatorInstance) {
      if (!creatorInstance) {
        console.warn('attachDescriptionLimit: creatorInstance is falsy');
        return;
      }

      // handler used for onValidateProperty-like APIs
      const validateHandler = (sender, options) => {
        try {
          if (
            options &&
            options.obj &&
            typeof options.obj.getType === 'function' &&
            options.obj.getType() === 'survey' &&
            options.propertyName === 'description'
          ) {
            const val = options.value || '';
            if (val.length > 1000) {
              options.value = val.substring(0, 1000);
              options.error = 'Max 1000 characters allowed.';
              toast.warn('Description cannot exceed 1000 characters (max 1000).');
            }
          }
        } catch (err) {
          console.error('validateHandler error', err);
        }
      };

      // handler used for onPropertyValueChanging-like APIs
      const changingHandler = (sender, options) => {
        try {
          if (
            options &&
            options.obj &&
            typeof options.obj.getType === 'function' &&
            options.obj.getType() === 'survey' &&
            options.propertyName === 'description'
          ) {
            const newValue = options.newValue || '';
            if (newValue.length > 1000) {
              options.newValue = newValue.substring(0, 1000);
              toast.warn('Description cannot exceed 1000 characters (max 1000).');
            }
          }
        } catch (err) {
          console.error('changingHandler error', err);
        }
      };

      // Try multiple event names (v1 and v2 variants). Attach where available.
      try {
        if (creatorInstance.onValidateProperty && typeof creatorInstance.onValidateProperty.add === 'function') {
          creatorInstance.onValidateProperty.add(validateHandler);
          console.info('attachDescriptionLimit: attached to onValidateProperty');
          return;
        }

        if (creatorInstance.onPropertyValueChanging && typeof creatorInstance.onPropertyValueChanging.add === 'function') {
          creatorInstance.onPropertyValueChanging.add(changingHandler);
          console.info('attachDescriptionLimit: attached to onPropertyValueChanging');
          return;
        }

        if (creatorInstance.onPropertyValueChanged && typeof creatorInstance.onPropertyValueChanged.add === 'function') {
          creatorInstance.onPropertyValueChanged.add(changingHandler);
          console.info('attachDescriptionLimit: attached to onPropertyValueChanged');
          return;
        }

        if (creatorInstance.onPropertyValidation && typeof creatorInstance.onPropertyValidation.add === 'function') {
          creatorInstance.onPropertyValidation.add((s, opts) => {
            // older variant sometimes sends opts differently
            validateHandler(s, opts);
          });
          console.info('attachDescriptionLimit: attached to onPropertyValidation');
          return;
        }

        // Last-resort: try onValidateProperty on creator.creator (some wrappers nest the real instance)
        if (creatorInstance.creator && creatorInstance.creator.onValidateProperty && typeof creatorInstance.creator.onValidateProperty.add === 'function') {
          creatorInstance.creator.onValidateProperty.add(validateHandler);
          console.info('attachDescriptionLimit: attached to creator.creator.onValidateProperty');
          return;
        }

        // If nothing matched — log keys to help debugging
        console.warn('attachDescriptionLimit: no matching event found on surveyCreator. Available keys:', Object.keys(creatorInstance));
        toast.error('Unable to attach description length limit — survey creator events unavailable. Check console.');
      } catch (err) {
        console.error('attachDescriptionLimit: unexpected error:', err);
        toast.error('Error attaching description validation. See console.');
      }
    })(surveyCreator);

    // Add title change listener
    surveyCreator.onPropertyChanged?.add((sender, options) => {
      if (options.name === 'title') {
        console.log('Title changed to:', options.newValue);
        surveyCreator.saveSurvey();
        setHasUnsavedChanges(true);
      }
    });

    // Add general change listener
    surveyCreator.onModified?.add(() => {
      try {
        setHasUnsavedChanges(
          JSON.stringify(JSON.parse(surveyCreator.text)) !== JSON.stringify(JSON.parse(initialJson))
        );
      } catch (e) {
        console.error('Error parsing JSON in onModified:', e);
      }
    });
    surveyCreator.onModified?.add(() => {
  try {
    const sjsJson = JSON.parse(surveyCreator.text);
    logoRef.current = sjsJson.logo || logoRef.current;
    const hiddenMap = {};
    (sjsJson.pages || []).forEach((page) => {
      const hidden = (page.elements || []).filter(el => el.visible === false || el.name?.startsWith('__'));
      if (hidden.length > 0) {
        hiddenMap[page.name] = hidden;
      }
    });
    hiddenQuestionsRef.current = hiddenMap;
    const pages = translateFromSurveyJS(sjsJson);
    const translated = {
  metadata: { title: sjsJson.title || '', description: sjsJson.description || '', logo: sjsJson.logo || '' },
  pages: pages,
  rules: [],
  settings: {
    oneResponsePerEmail: !!sjsJson.oneResponsePerEmail,
    limitOneResponse: !!sjsJson.limitOneResponse,
    readOnly: !!sjsJson.readOnly,
    language: sjsJson.locale || 'en',
    showTitleDescription: sjsJson.showTitleDescription !== undefined ? sjsJson.showTitleDescription : true,
    widthMode: sjsJson.widthMode || 'auto',
  },
};

    setNavritiSchema(translated);
    navritiSchemaRef.current = translated;
  } catch (e) {
    console.error('Error syncing navritiSchema:', e);
  }
});
    if (isViewer) {
        surveyCreator.onModified?.add(() => {
          toast.info("You have view-only access");
        });
      }


    setCreator(surveyCreator);
    setLoad(true);
  } catch (error) {
    console.error('Error in initializeSurveyCreator:', error.message);
    setError(error.message);
    toast.error(`Failed to load form: ${error.message}`);
    setLoad(true);
  }
};

initializeSurveyCreator();
}, [location.search]);

    
    useEffect(() => {
      if (!creator) return;

      if (accessRole === "VIEWER") {
        creator.readOnly = true;
        creator.showToolbox = false;
        creator.showPropertyGrid = false;
        creator.isAutoSave = false;
      } else {
        creator.readOnly = false;
        creator.showToolbox = true;
        creator.showPropertyGrid = true;
      }
    }, [accessRole, creator]);

  const saveFormData = async (isDraft, formId = null) => {
  try {
    const token = getCookie('token');
    await creator.saveSurvey();
    const creatorJSON = JSON.parse(creator.text);

    if (!creatorJSON.title) {
      toast.error("Form name is missing. Please add a form name before saving.");
      return false;
    }

    const requestBody = {
      formData: creatorJSON,
      formName: creatorJSON.title,
      type: type,
      isDraft: isDraft,
      email: getCookie('email'),
    };

    // Decide whether to update or create a new draft
    const isExistingDraft = formData?.isDraft && id;

    if (isExistingDraft) {
      requestBody.id = id; // backend expects id for update
    } else if (formId) {
      requestBody.formId = formId; // maintain your existing logic
    }

    if (!isDraft) {
      if (type === 'recurring') {
        requestBody.recurrence = { duration, frequency };
      } else if (type === 'one-time') {
        requestBody.dateRange = [new Date(startDate).toISOString(), new Date(endDate).toISOString()];
      } else if (type === 'open') {
        requestBody.dateRange = [];
      }
    }

    // Use UPDATE API if editing a draft, otherwise SAVE
    const url = isExistingDraft ? URLs.FORM_UPDATE : URL_FORM_SAVE;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });

    if (response.ok) {
      let res = await response.json();

      // Update local state so future saves work correctly
      setId(res.savedFormData.id);
      setFormData(prev => ({ ...prev, isDraft: true }));

      if (isDraft) {
        toast.success(messages.saveFormSuccessMessage);
        setHasUnsavedChanges(false);
      } else {
        toast.success(messages.saveFormLinkSuccessMessage, { autoClose: 3000 });
        const currentUrl = window.location.href;
        const baseUrl = new URL(currentUrl).origin;
        const encryptedId = await encryptFormId(res.savedFormData.id);
        setLink(`${baseUrl}/forms?id=${encryptedId}`);
        await updateLink(res.savedFormData.id);
      }

      return res.savedFormData.id;
    } else {
      const errorData = await response.json();
      console.error('formSave error response:', errorData);
      toast.error(errorData.error || messages.saveFormErrorMessage);
      return false;
    }
  } catch (error) {
    console.error('Error saving form data:', error);
    toast.error(messages.saveFormErrorMessage);
    return false;
  }
};


  const handleSaveButtonClick = async () => {
   
    if (!creator) {
      console.error('handleSaveButtonClick: Survey Creator is not initialized');
      toast.error('Survey Creator is not initialized.');
      return;
    }
    if (isSavingDraft) {
     
      return;
    }
    setIsSavingDraft(true);
    try {
      await creator.saveSurvey();
      
      const result = await saveFormData(true, id);
      
    } catch (error) {
      console.error('handleSaveButtonClick: Error saving form data:', error);
      toast.error(messages.saveFormErrorMessage);
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handlePublishButtonClick = () => {
   
    if (isPublishing) {
     
      return;
    }
    if (!creator) {
      console.error('handlePublishButtonClick: Survey Creator is not initialized');
      toast.error('Survey Creator is not initialized.');
      return;
    }
    creator.saveSurvey();
    const creatorJSON = JSON.parse(creator.text);
   
    if (!creatorJSON.title) {
      console.error('handlePublishButtonClick: Form name missing');
      toast.error("Form name is missing. Please add a form name before saving.");
      return;
    }
    setModalIsOpen(true);
  };

  const encryptFormId = async (formId) => {
    try {
      const token = getCookie('token');
      const response = await fetch(URLs.encryptId, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ id: formId }),
      });

      if (response.ok) {
        const { encryptedId } = await response.json();
        return encryptedId;
      } else {
        throw new Error('Failed to encrypt form ID');
      }
    } catch (error) {
      console.error('Error encrypting form ID:', error);
      toast.error('Failed to generate form link');
      return formId;
    }
  };

  const publishFormWithDates = async (effectiveStartDate, effectiveEndDate) => {
  if (!type) {
    toast.error('Please select a form type.');
    return;
  }
  if (type === 'recurring' && (!duration || !frequency)) {
    toast.error(duration ? messages.messages.errorFrequency : messages.messages.errorDuration);
    return;
  }
  if (type === 'recurring' && duration > frequencyDays[frequency]) {
    toast.error(`Duration (${duration} days) cannot exceed ${frequencyDays[frequency]} days for ${frequency} frequency.`);
    return;
  }
  if (type === 'one-time' && (!effectiveStartDate || !effectiveEndDate)) {
    toast.error(messages.endDateMissingMessage);
    return;
  }
  setIsPublishing(true);
  try {
    await creator.saveSurvey();
    const creatorJSON = JSON.parse(creator.text);

    if (!creatorJSON.title) {
      toast.error("Form name is missing. Please add a form name before saving.");
      return;
    }
    const requestBody = {
      formData: creatorJSON,
      formName: creatorJSON.title,
      type: type,
      isDraft: false,
      email: getCookie('email'),
    };
    if (type === 'recurring') {
      requestBody.recurrence = { duration, frequency };
    } else if (type === 'one-time') {
      requestBody.dateRange = [new Date(effectiveStartDate).toISOString(), new Date(effectiveEndDate).toISOString()];
    } else {
      requestBody.dateRange = [];
    }
    const token = getCookie('token');
    
    // Use formUpdate if id exists, otherwise use formSave
    const url = id ? URLs.FORM_UPDATE : URL_FORM_SAVE;
    if (id) {
      requestBody.id = id; // Include id for update
    }

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      credentials: 'include',
      body: JSON.stringify(requestBody),
    });
    if (response.ok) {
      let res = await response.json();
      toast.success(messages.saveFormLinkSuccessMessage, { autoClose: 3000 });
      const currentUrl = window.location.href;
      const baseUrl = new URL(currentUrl).origin;
      const encryptedId = await encryptFormId(res.savedFormData.id);
      setLink(`${baseUrl}/forms?id=${encryptedId}`);
      await updateLink(res.savedFormData.id);
      setModalIsOpen(false);
      setType('');
      setStartDate('');
      setEndDate('');
      setDuration(1);
      setFrequency('');
      setHasUnsavedChanges(false);
      setId(res.savedFormData.id); // Update id state
    } else {
      const errorData = await response.json();
      console.error('formSave/formUpdate error response:', errorData);
      toast.error(errorData.error || messages.saveFormErrorMessage);
    }
  } catch (error) {
    console.error('Error saving/updating form data:', error);
    toast.error(messages.saveFormErrorMessage);
  } finally {
    setIsPublishing(false);
  }
};

  const updateLink = async (id) => {
    const currentUrl = window.location.href;
    const baseUrl = new URL(currentUrl).origin;
    try {
      const token = getCookie('token');
      const encryptedId = await encryptFormId(id);
      const response = await fetch(URL_UPDATE_LINK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'include',
        body: JSON.stringify({
          formId: id,
          formLink: `${baseUrl}/forms?id=${encryptedId}`,
        }),
      });
      if (response.ok) {
        setLink(`${baseUrl}/forms?id=${encryptedId}`);
      } else {
        toast.error('Failed to save the form link. Please try again later.');
      }
    } catch (error) {
      console.error('Error saving form link:', error);
      toast.error('An error occurred while saving the form link.');
    }
  };

  const handleModalClose = () => {
    setModalIsOpen(false);
    setType('');
    setStartDate('');
    setEndDate('');
    setDuration(1);
    setFrequency('');
  };

  const handleSubmitModal = async () => {
    let effectiveStartDate = startDate;
    let effectiveEndDate = endDate;
    if (type === 'recurring' && (!frequency || !duration)) {
      toast.error(duration ? messages.messages.errorFrequency : messages.messages.errorDuration);
      return;
    }
    if (type === 'recurring' && duration > frequencyDays[frequency]) {
  toast.error(`Duration (${duration} days) cannot exceed ${frequencyDays[frequency]} days for ${frequency} frequency.`);
  return;
}
    if (type === 'one-time' && (!startDate || !endDate)) {
      toast.error(messages.endDateMissingMessage);
      return;
    }
    if (type === 'open') {
      const today = new Date();
      const nextYear = new Date(today);
      nextYear.setFullYear(today.getFullYear() + 1);
      effectiveStartDate = today.toISOString().split('T')[0];
      effectiveEndDate = nextYear.toISOString().split('T')[0];
      setStartDate(effectiveStartDate);
      setEndDate(effectiveEndDate);
    }
    await publishFormWithDates(effectiveStartDate, effectiveEndDate);
  };

  const handleBackToHome = () => {
    if (hasUnsavedChanges && formData?.isDraft) {
      setConfirmModalOpen(true);
    } else {
      navigate('/');
    }
  };

  const handleConfirmDontSave = () => {
    setConfirmModalOpen(false);
    navigate('/');
  };

  const handleConfirmSave = async () => {
    if (!creator) {
      toast.error('Survey Creator is not initialized.');
      setConfirmModalOpen(false);
      return;
    }
    setIsSavingDraft(true);
    try {
      await creator.saveSurvey();
      const saved = await saveFormData(true, id);
      if (saved) {
        setConfirmModalOpen(false);
        navigate('/');
      }
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleConfirmCancel = () => {
    setConfirmModalOpen(false);
  };

const isViewer = accessRole === "VIEWER";
const isEditor = accessRole === "EDITOR";
const isOwner = accessRole === "OWNER";

const canEdit = isOwner || isEditor;
const canSave = canEdit;
const canPublish = canEdit;
  return (
  <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
  {load && creator ? (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* <SurveyCreatorComponent creator={creator} /> */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          backgroundColor: '#ffffffff',
          borderBottom: '1px solid #ddd',
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
          height: '60px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Flex align="center">
              <a href='/'><Image src={logo} height={50} width={50} /></a>
          </Flex>
          <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#333' }}>
            Form Builder
          </h2>
          {accessRole !== "OWNER" && (
            <Badge
              color={accessRole === "EDITOR" ? "#4f85f1ff" : "#4db0e5ff"}
              variant="light"
              radius="md"
              size="sm"
            >
              {accessRole === "EDITOR" ? "Editor access" : "Viewer access"}
            </Badge>
          )}



        </div>
        <div style={{ display: 'flex', gap: '6px', flexShrink: 0, height: '60%' }}>
          {/* {isViewer && (
            <span style={{ color: "#999", fontSize: "14px", margin: "14px" }}>
              You have view-only access
            </span>
          )} */}

          <Button
            onClick={handleBackToHome}
            variant='outline'
            style={{
              borderRadius: '9px',
              margin: '0',
              height: '100%',
            }}
          >
            Back to Home
          </Button>
          <Button
            variant={versionModalOpen ? "filled" : "outline"}
            onClick={() => setVersionModalOpen(true)}
            disabled={!id}
            style={{
              borderRadius: '9px',
              margin: '0',
              height: '100%',
            }}
          >
            Version History
          </Button>
          <Button
            onClick={handleSaveButtonClick}
            disabled={isSavingDraft || isPublishing || !canSave}
            variant={confirmModalOpen ? "filled" : "outline"}
            title={!canSave ? "View-only access" : ""}
            loading={isSavingDraft}
            style={{
              borderRadius: '9px',
              margin: '0',
              height: '100%',
            }}
          >
            Save as draft
          </Button>
          <Button
            onClick={handlePublishButtonClick}
            disabled={isSavingDraft || isPublishing || !canPublish}
            title={!canPublish ? "View-only access" : ""}
            loading={isPublishing}
            variant={modalIsOpen ? "filled" : "outline"}            
            style={{
              borderRadius: '9px',
              margin: '0',
              height: '100%',
            }}
          >
            Publish Form
          </Button>
          {link &&
          <Button
              onClick={() => setLinkModalOpen(true)}
              variant={linkModalOpen ? "filled" : "outline"}
              style={{
                borderRadius: '9px',
                margin: '0',
                height: '100%',
              }}
            >
              View Form Link
            </Button>}
        </div>

      </div>
      <div style={{ display: 'flex', gap: '8px', padding: '8px 24px', borderBottom: '1px solid #eee' }}>
  <Button variant={activeTab === 'designer' ? 'filled' : 'outline'} onClick={() => handleTabChange('designer')}>Designer</Button>
  <Button variant={activeTab === 'logic' ? 'filled' : 'outline'} onClick={() => handleTabChange('logic')}>Logic</Button>
  <Button variant={activeTab === 'json' ? 'filled' : 'outline'} onClick={() => handleTabChange('json')}>JSON</Button>
</div>
<div style={{ flex: 1 }}>
  <div style={{ display: activeTab === 'designer' ? 'block' : 'none', height: '100%' }}>
  <SurveyCreatorComponent key={designerKey} creator={creator} onLogoUpload={handleLogoUpload} />
</div>
  {activeTab === 'logic' && (
  <LogicEditor
    schema={navritiSchema}
    onChange={(newSchema) => {

      if (navritiSchemaRef.current.pages?.length > 0 && newSchema.pages?.length === 0) {
        return;
      }
      setNavritiSchema(newSchema);
      navritiSchemaRef.current = newSchema;
      const sjsJson = translateToSurveyJS(newSchema.pages, newSchema.metadata, newSchema.settings);
      sjsJson.logo = logoRef.current;
      (sjsJson.pages || []).forEach((page) => {
        const hidden = hiddenQuestionsRef.current[page.name];
        if (hidden && hidden.length > 0) {
          const existingNames = new Set((page.elements || []).map(el => el.name));
          hidden.forEach((h) => {
            if (!existingNames.has(h.name)) {
              page.elements.push(h);
            }
          });
        }
      });
      creator.text = '{}';
      creator.text = JSON.stringify(sjsJson);
    }}
  />
)}
  {activeTab === 'json' && (
  <JsonView
    schema={navritiSchema}
    onChange={(newSchema) => {

      if (navritiSchemaRef.current.pages?.length > 0 && newSchema.pages?.length === 0) {
        return;
      }
      setNavritiSchema(newSchema);
      navritiSchemaRef.current = newSchema;
      const sjsJson = translateToSurveyJS(newSchema.pages, newSchema.metadata, newSchema.settings);
      sjsJson.logo = logoRef.current;
      (sjsJson.pages || []).forEach((page) => {
        const hidden = hiddenQuestionsRef.current[page.name];
        if (hidden && hidden.length > 0) {
          const existingNames = new Set((page.elements || []).map(el => el.name));
          hidden.forEach((h) => {
            if (!existingNames.has(h.name)) {
              page.elements.push(h);
            }
          });
        }
      });
      creator.text = '{}';
      creator.text = JSON.stringify(sjsJson);
    }}
  />
)}
</div>

      <Modal opened={modalIsOpen} onClose={handleModalClose} title="Choose Type"
      overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}>
        <Select
          label="Form Type"
          placeholder="Pick one"
          data={messages.formTypes}
          value={type}
          onChange={(value) => setType(value)}
        />
        {type === 'one-time' && (
          <div>
            <TextInput
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(event) => setStartDate(event.currentTarget.value)}
              style={{ marginBottom: '10px' }}
            />
            <TextInput
              label="End Date"
              type="date"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              style={{ marginBottom: '10px' }}
            />
          </div>
        )}
        {type === 'recurring' && (
          <div>
            <Select
              label="Recurrence Frequency"
              placeholder="Select frequency"
              data={messages.recurrenceFrequencies}
              value={frequency}
              onChange={setFrequency}
              style={{ marginBottom: '10px' }}
            />
            <NumberInput
              label="Duration (days)"
              value={duration}
              onChange={(value) => setDuration(value)}
              min={1}
              max={frequency ? frequencyDays[frequency] : 30}
              style={{ marginBottom: '10px' }}
            />
          </div>
        )}
        <Button
          onClick={handleSubmitModal}
          style={{ marginTop: '10px' }}
          disabled={isPublishing}
          loading={isPublishing}
        >
          Submit
        </Button>
        <Button onClick={handleModalClose} variant="light" style={{ marginTop: '10px' }}>
          Cancel
        </Button>
      </Modal>

      <Modal opened={confirmModalOpen} onClose={handleConfirmCancel} title="Unsaved Changes" centered
       overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}>
        <p>Do you want to save your changes before leaving?</p>
        <Button onClick={handleConfirmDontSave} color="blue" style={{ marginRight: '10px' }}>
          Don't Save
        </Button>
        <Button
          onClick={handleConfirmSave}
          style={{ marginRight: '10px' }}
          disabled={isSavingDraft}
          loading={isSavingDraft}
        >
          Save
        </Button>
        <Button onClick={handleConfirmCancel} variant="light">
          Cancel
        </Button>
      </Modal>
        <Modal
          opened={versionModalOpen}
          onClose={() => setVersionModalOpen(false)}
          title="Version History"
          overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}  >
          <FormVersionHistory
            formId={id}
            canRestore={canEdit}
            onRestored={async (newFormId) => {
              const encryptedId = await encryptFormId(newFormId);
              navigate(`/viewForm?id=${encryptedId}`);
            }}
          />
        </Modal>

          <Modal
                opened={linkModalOpen}
                onClose={() => setLinkModalOpen(false)}
                title="Form Link"
                centered
                overlayProps={{
                backgroundOpacity: 0.55,
                blur: 3,
              }}
              >
                <p style={{ wordBreak: 'break-all', textAlign: 'center' }}>
                  <a href={link} target="_blank" rel="noopener noreferrer" style={{ color: '#17a387ff' }}>
                    {link}
                  </a>
                </p>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(link);
                    setLinkModalOpen(false);
                    toast.success('Form link copied to clipboard!');
                  }}
                  fullWidth
                  style={{ marginTop: '15px' }}
                >
                  Copy Link
                </Button>
                <Button
                  variant="light"
                  fullWidth
                  style={{ marginTop: '10px' }}
                  onClick={() => setLinkModalOpen(false)}
                >
                  Close
                </Button>
              </Modal>

      <ToastContainer />
    </div>
  ) : error ? (
    <p style={{ color: 'red', fontSize: '25px', textAlign: 'center', marginTop: '20px' }}>
      Error: {error}
    </p>
  ) : (
    <p style={{color: 'gray', fontSize: '25px', textAlign: 'center', marginTop: '20px'}}>Loading...</p>
  )}
</div>
  );
}